import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { OrderState, CartItem, MenuItem, CreateCartRequestBody, CreateCartOrderItem } from '../types';
import menuService from '../data/menuService';
import apiService from '../services/apiService';
import { apiConfig, applyApiConfigOverrides } from '../config/api';

// Async thunk for loading runtime API config from CMS settings keys
export const loadRuntimeApiConfig = createAsyncThunk(
  'order/loadRuntimeApiConfig',
  async (_, { rejectWithValue }) => {
    try {
      const parseBooleanSetting = (value: string): boolean => {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        return false;
      };

      const [
        menuKey,
        terminalId,
        restaurantKey,
        partnerKey,
        menuVersionRaw,
        brandKey,
        payAtCounterRaw,
        payWithCardRaw,
      ] = await Promise.all([
        apiService.fetchCmsSettingByKey('kiosk-menu-key'),
        apiService.fetchCmsSettingByKey('kiosk-terminal-id'),
        apiService.fetchCmsSettingByKey('kiosk-restaurant-key'),
        apiService.fetchCmsSettingByKey('kiosk-partner-key'),
        apiService.fetchCmsSettingByKey('kiosk-menu-version'),
        apiService.fetchCmsSettingByKey('kiosk-brand-key'),
        apiService.fetchCmsSettingByKey('kiosk-pay-at-counter'),
        apiService.fetchCmsSettingByKey('kiosk-pay-with-card'),
      ]);

      const parsedMenuVersion = Number(menuVersionRaw);
      const overrides: Partial<typeof apiConfig> = {};

      if (menuKey) overrides.menuKey = menuKey;
      if (terminalId) overrides.terminalId = terminalId;
      if (restaurantKey) overrides.restaurantKey = restaurantKey;
      if (partnerKey) overrides.partnerKey = partnerKey;
      if (brandKey) overrides.brandKey = brandKey;
      if (Number.isFinite(parsedMenuVersion)) overrides.menuVersion = parsedMenuVersion;

      applyApiConfigOverrides(overrides);
      return {
        overrides,
        payAtCounterEnabled: parseBooleanSetting(payAtCounterRaw),
        payWithCardEnabled: parseBooleanSetting(payWithCardRaw),
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load runtime API config');
    }
  }
);

// Async thunk for loading menu data
export const loadMenuData = createAsyncThunk(
  'order/loadMenuData',
  async (_, { rejectWithValue }) => {
    try {
      await menuService.initializeMenuData();
      
      const menuCategories = menuService.getMenuCategories();
      const menuItems = menuService.getAllMenuItems();
      const firstCategoryId = menuService.getFirstCategoryId();

      return {
        menuCategories,
        menuItems,
        firstCategoryId
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load menu data');
    }
  }
);

// Async thunk for loading CMS onboarding screen images
export const loadOnboardingScreens = createAsyncThunk(
  'order/loadOnboardingScreens',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.fetchOnboardingScreens();
      const welcome = response.data.find((s) => s.title === 'Welcome to Panarottis');
      const loyalty = response.data.find((s) => s.title === 'Reward yourself');
      return {
        welcomeImage: welcome?.image,
        loyaltyImage: loyalty?.image,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load onboarding screens');
    }
  }
);

// Async thunk for refreshing menu data
export const refreshMenuData = createAsyncThunk(
  'order/refreshMenuData',
  async (_, { rejectWithValue }) => {
    try {
      await menuService.refreshMenuData();
      
      const menuCategories = menuService.getMenuCategories();
      const menuItems = menuService.getAllMenuItems();
      const firstCategoryId = menuService.getFirstCategoryId();

      return {
        menuCategories,
        menuItems,
        firstCategoryId
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh menu data');
    }
  }
);

/** Build create-cart request body from cart items, config, and optional loyalty profile. */
function buildCreateCartBody(
  cart: CartItem[],
  fulfilmentChannel: string,
  loyaltyProfileKey?: string | null,
  loyaltyProfileBrandGroupKey?: string | null,
  loyaltyFirstName?: string | null
): CreateCartRequestBody {
  const menuKey = apiConfig.menuKey.toUpperCase();
  const menuVersion = apiConfig.menuVersion;

  const items: CreateCartOrderItem[] = cart.map((item) => {
    const orderItem: CreateCartOrderItem = {
      menuKey,
      menuVersion,
      menuItemKey: parseInt(item.menuItem.id, 10),
      displayName: item.menuItem.displayName,
      currency: 'ZAR',
      price: item.menuItem.price,
      quantity: item.quantity,
      specialInstructions: null,
    };
    if (item.selectedOptions?.length) {
      const options = item.selectedOptions
        .filter((opt) => opt.optionSetKey != null)
        .map((opt) => ({
          optionSetKey: opt.optionSetKey!,
          optionSetItemKey: parseInt(opt.id, 10),
          displayName: opt.displayName,
          currency: 'ZAR',
          price: opt.price,
        }));
      if (options.length > 0) orderItem.options = options;
    }
    return orderItem;
  });

  const body: CreateCartRequestBody = {
    partnerKey: apiConfig.partnerKey,
    restaurantKey: apiConfig.restaurantKey,
    platform: 'Kiosk',
    orderingChannel: 'Kiosk',
    fulfilmentChannel,
    customerFirstName: 'Kiosk',
    customerLastName: 'Guest',
    customerEmailAddress: 'kiosk@store.local',
    customerMobilePhone: null,
    customer: {
      firstName: 'Kiosk',
      lastName: 'Guest',
      emailAddress: 'kiosk@store.local',
      mobilePhone: null,
    },
    currency: 'ZAR',
    menuKey,
    menuVersion,
    items,
    note: null,
  };

  if (loyaltyProfileKey && loyaltyProfileBrandGroupKey && loyaltyFirstName) {
    body.customer.profileKey = loyaltyProfileKey;
    body.customer.profileBrandGroupKey = loyaltyProfileBrandGroupKey;
    body.customer.firstName = loyaltyFirstName;
    // Set default mobile country code when loyalty profile is present
    body.customer.mobileCountryCode = 'ZA';
  }

  return body;
}

// Async thunk for creating cart (when user taps Pay Here or Pay at Counter)
export const createCart = createAsyncThunk(
  'order/createCart',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { order: OrderState };
    const {
      cart,
      diningOption,
      loyaltyProfileKey,
      loyaltyProfileBrandGroupKey,
      loyaltyFirstName,
    } = state.order;
    if (!cart.length) {
      return rejectWithValue('Cart is empty');
    }
    const fulfilmentChannel = diningOption === 'takeaway' ? 'takeaway' : 'sit-down';
    const body = buildCreateCartBody(
      cart,
      fulfilmentChannel,
      loyaltyProfileKey,
      loyaltyProfileBrandGroupKey,
      loyaltyFirstName
    );
    const response = await apiService.createCart(body);
    return { cartId: response.data.cartId };
  }
);

// Async thunk for adding payment to cart (after cart is created)
export const addCartPayment = createAsyncThunk(
  'order/addCartPayment',
  async (cartId: string, { rejectWithValue }) => {
    const body = { TerminalID: apiConfig.terminalId };
    const response = await apiService.addCartPayment(cartId, body);
    return { paymentId: response.data.paymentId };
  }
);

// Async thunk for submitting cart into an order (after payment for card, directly for counter)
export const submitCart = createAsyncThunk(
  'order/submitCart',
  async (cartId: string, { rejectWithValue }) => {
    try {
      const response = await apiService.submitCart(cartId);
      return { orderId: response.data.orderId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit cart');
    }
  }
);

const initialState: OrderState = {
  diningOption: null,
  cart: [],
  currentScreen: 'welcome',
  selectedCategory: '',
  selectedMenuItem: null,
  menuCategories: [],
  menuItems: [],
  isLoading: false,
  error: null,
  loyaltyCode: null,
  loyaltyProfileKey: null,
  loyaltyProfileBrandGroupKey: null,
  loyaltyFirstName: null,
  currentOrderNumber: null,
  currentOrderTimestamp: null,
  cartId: null,
  isCreatingCart: false,
  isAddingPayment: false,
  isSubmittingCart: false,
  paymentId: null,
  currentOrderId: null,
  isCurrentOrderUnpaid: false,
  payWithCardEnabled: false,
  payAtCounterEnabled: false,
  onboardingImages: {},
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setDiningOption: (state, action: PayloadAction<'eat-in' | 'takeaway'>) => {
      state.diningOption = action.payload;
      state.currentScreen = 'menu';
    },
    setCurrentScreen: (state, action: PayloadAction<OrderState['currentScreen']>) => {
      state.currentScreen = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string>) => {
      state.selectedCategory = action.payload;
    },
    setSelectedMenuItem: (state, action: PayloadAction<MenuItem | null>) => {
      state.selectedMenuItem = action.payload;
    },
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.cart.find(
        item => item.menuItem.id === action.payload.menuItem.id &&
        JSON.stringify(item.selectedOptions) === JSON.stringify(action.payload.selectedOptions)
      );
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        existingItem.totalPrice += action.payload.totalPrice;
      } else {
        state.cart.push(action.payload);
      }
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter(item => item.id !== action.payload);
    },
    updateCartItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.cart.find(item => item.id === action.payload.id);
      if (item) {
        item.quantity = action.payload.quantity;
        item.totalPrice = (item.menuItem.price +
          item.selectedOptions.reduce((sum, option) => sum + option.price, 0)) * action.payload.quantity;
      }
    },
    clearCart: (state) => {
      state.cart = [];
      state.currentOrderNumber = null;
      state.currentOrderTimestamp = null;
      state.cartId = null;
      state.paymentId = null;
      state.currentOrderId = null;
      state.isCurrentOrderUnpaid = false;
      state.error = null;
      state.loyaltyProfileKey = null;
      state.loyaltyProfileBrandGroupKey = null;
      state.loyaltyFirstName = null;
    },
    setCartId: (state, action: PayloadAction<string | null>) => {
      state.cartId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoyaltyCode: (state, action: PayloadAction<string | null>) => {
      state.loyaltyCode = action.payload;
      if (action.payload === null) {
        state.loyaltyProfileKey = null;
        state.loyaltyProfileBrandGroupKey = null;
        state.loyaltyFirstName = null;
      }
    },
    setLoyaltyProfile: (state, action: PayloadAction<{ profileKey: string; profileBrandGroupKey: string; firstName: string }>) => {
      state.loyaltyProfileKey = action.payload.profileKey;
      state.loyaltyProfileBrandGroupKey = action.payload.profileBrandGroupKey;
      state.loyaltyFirstName = action.payload.firstName;
    },
    setOrderMetadata: (state, action: PayloadAction<{ orderNumber: string; timestamp: string }>) => {
      state.currentOrderNumber = action.payload.orderNumber;
      state.currentOrderTimestamp = action.payload.timestamp;
    },
    clearOrderMetadata: (state) => {
      state.currentOrderNumber = null;
      state.currentOrderTimestamp = null;
      state.currentOrderId = null;
      state.isCurrentOrderUnpaid = false;
    },
    setCurrentOrderUnpaid: (state, action: PayloadAction<boolean>) => {
      state.isCurrentOrderUnpaid = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load menu data
      .addCase(loadMenuData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadMenuData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menuCategories = action.payload.menuCategories;
        state.menuItems = action.payload.menuItems;
        if (!state.selectedCategory && action.payload.firstCategoryId) {
          state.selectedCategory = action.payload.firstCategoryId;
        }
        state.error = null;
      })
      .addCase(loadMenuData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Refresh menu data
      .addCase(refreshMenuData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshMenuData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menuCategories = action.payload.menuCategories;
        state.menuItems = action.payload.menuItems;
        state.error = null;
      })
      .addCase(refreshMenuData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create cart
      .addCase(createCart.pending, (state) => {
        state.isCreatingCart = true;
        state.error = null;
      })
      .addCase(createCart.fulfilled, (state, action) => {
        state.isCreatingCart = false;
        state.cartId = action.payload.cartId;
        state.error = null;
      })
      .addCase(createCart.rejected, (state, action) => {
        state.isCreatingCart = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Failed to create cart';
      })
      // Add cart payment
      .addCase(addCartPayment.pending, (state) => {
        state.isAddingPayment = true;
        state.error = null;
      })
      .addCase(addCartPayment.fulfilled, (state, action) => {
        state.isAddingPayment = false;
        state.paymentId = action.payload.paymentId;
        state.error = null;
      })
      .addCase(addCartPayment.rejected, (state, action) => {
        state.isAddingPayment = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Failed to add payment';
      })
      // Submit cart
      .addCase(submitCart.pending, (state) => {
        state.isSubmittingCart = true;
        state.error = null;
      })
      .addCase(submitCart.fulfilled, (state, action) => {
        state.isSubmittingCart = false;
        state.currentOrderId = action.payload.orderId;
        state.error = null;
      })
      .addCase(submitCart.rejected, (state, action) => {
        state.isSubmittingCart = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Failed to submit cart';
      })
      // Load onboarding screens (CMS images)
      .addCase(loadOnboardingScreens.fulfilled, (state, action) => {
        state.onboardingImages = action.payload;
      })
      // Load runtime config (CMS settings)
      .addCase(loadRuntimeApiConfig.fulfilled, (state, action) => {
        state.payAtCounterEnabled = action.payload.payAtCounterEnabled;
        state.payWithCardEnabled = action.payload.payWithCardEnabled;
      });
  },
});

export const {
  setDiningOption,
  setCurrentScreen,
  setSelectedCategory,
  setSelectedMenuItem,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  clearError,
  setLoyaltyCode,
  setLoyaltyProfile,
  setOrderMetadata,
  setCurrentOrderUnpaid,
  clearOrderMetadata,
  setCartId,
} = orderSlice.actions;

export default orderSlice.reducer; 